import React from "react";
import { Box, Skeleton } from "@mui/material";
import PageContainer from "../PageContainer";

type LibrarySkeletonProps = {
  label: string;
  /** Library and Resources open with a hero; grade and story pages with a section title. */
  top: "hero" | "title";
  cards?: number;
  cardHeight?: number;
  gridSx?: object;
};

const THREE_COLUMN_GRID_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
  gap: 2,
} as const;

/**
 * A Library page (Library, Worksheets, Resources and the pages under them)
 * while its code downloads: breadcrumb, hero or title, then grey cards.
 */
const LibrarySkeleton: React.FC<LibrarySkeletonProps> = ({
  label,
  top,
  cards = 3,
  cardHeight = 292,
  gridSx = THREE_COLUMN_GRID_SX,
}) => (
  <PageContainer>
    <Box role="status" aria-label={label} sx={{ maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ mt: 1, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <Skeleton variant="circular" width={22} height={22} />
        <Skeleton variant="text" width={64} sx={{ fontSize: "1rem" }} />
      </Box>
      {top === "hero" ? (
        <Skeleton
          variant="rounded"
          sx={{
            height: { xs: 240, md: 276 },
            mb: 2.5,
            mt: { xs: 2, md: 0 },
            borderRadius: { xs: 2, md: 3 },
          }}
        />
      ) : (
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={240} sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }} />
          <Skeleton variant="text" sx={{ fontSize: "1rem", maxWidth: 520 }} />
        </Box>
      )}
      <Box sx={gridSx}>
        {Array.from({ length: cards }).map((_, card) => (
          <Skeleton key={card} variant="rounded" height={cardHeight} />
        ))}
      </Box>
    </Box>
  </PageContainer>
);

export default LibrarySkeleton;
